import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

const THEMES = [
  { id: "water", x: 12, y: 20, labelKey: "dreamMap.water", defaultLabel: "Vesi" },
  { id: "flying", x: 45, y: 5, labelKey: "dreamMap.flying", defaultLabel: "Lentäminen" },
  { id: "falling", x: 88, y: 18, labelKey: "dreamMap.falling", defaultLabel: "Putoaminen" },
  { id: "animals", x: 5, y: 55, labelKey: "dreamMap.animals", defaultLabel: "Eläimet" },
  { id: "people", x: 50, y: 42, labelKey: "dreamMap.people", defaultLabel: "Ihmiset" },
  { id: "house", x: 95, y: 52, labelKey: "dreamMap.house", defaultLabel: "Talo" },
  { id: "forest", x: 18, y: 90, labelKey: "dreamMap.forest", defaultLabel: "Metsä" },
  { id: "sea", x: 80, y: 88, labelKey: "dreamMap.sea", defaultLabel: "Meri" },
  { id: "ship", x: 68, y: 75, labelKey: "dreamMap.ship", defaultLabel: "Laiva" },
  { id: "car", x: 32, y: 70, labelKey: "dreamMap.car", defaultLabel: "Auto" },
  { id: "train", x: 55, y: 68, labelKey: "dreamMap.train", defaultLabel: "Juna" },
  { id: "airplane", x: 72, y: 10, labelKey: "dreamMap.airplane", defaultLabel: "Lentokone" },
  { id: "bicycle", x: 28, y: 35, labelKey: "dreamMap.bicycle", defaultLabel: "Polkupyörä" },
];

const EMOTIONS = [
  { id: "anxiety", x: 35, y: 35, labelKey: "dreamMap.anxiety", defaultLabel: "Ahdistus" },
  { id: "joy", x: 65, y: 20, labelKey: "dreamMap.joy", defaultLabel: "Ilo" },
  { id: "fear", x: 80, y: 48, labelKey: "dreamMap.fear", defaultLabel: "Pelko" },
  { id: "adventure", x: 18, y: 48, labelKey: "dreamMap.adventure", defaultLabel: "Seikkailu" },
  { id: "peace", x: 42, y: 72, labelKey: "dreamMap.peace", defaultLabel: "Rauha" },
  { id: "freedom", x: 60, y: 70, labelKey: "dreamMap.freedom", defaultLabel: "Vapaus" },
  { id: "confusion", x: 72, y: 42, labelKey: "dreamMap.confusion", defaultLabel: "Hämmennys" },
  { id: "safety", x: 30, y: 58, labelKey: "dreamMap.safety", defaultLabel: "Turvallisuus" },
];

const CONNECTIONS = [
  { from: "water", to: "anxiety", strength: 0.9 },
  { from: "water", to: "peace", strength: 0.7 },
  { from: "water", to: "freedom", strength: 0.5 },
  { from: "flying", to: "freedom", strength: 0.9 },
  { from: "flying", to: "joy", strength: 0.8 },
  { from: "flying", to: "adventure", strength: 0.6 },
  { from: "falling", to: "fear", strength: 0.9 },
  { from: "falling", to: "anxiety", strength: 0.8 },
  { from: "falling", to: "confusion", strength: 0.5 },
  { from: "animals", to: "safety", strength: 0.7 },
  { from: "animals", to: "fear", strength: 0.6 },
  { from: "animals", to: "adventure", strength: 0.5 },
  { from: "people", to: "confusion", strength: 0.7 },
  { from: "people", to: "joy", strength: 0.6 },
  { from: "people", to: "safety", strength: 0.6 },
  { from: "people", to: "anxiety", strength: 0.5 },
  { from: "house", to: "safety", strength: 0.8 },
  { from: "house", to: "confusion", strength: 0.6 },
  { from: "house", to: "fear", strength: 0.4 },
  { from: "forest", to: "adventure", strength: 0.8 },
  { from: "forest", to: "peace", strength: 0.7 },
  { from: "forest", to: "fear", strength: 0.5 },
  { from: "sea", to: "freedom", strength: 0.9 },
  { from: "sea", to: "peace", strength: 0.7 },
  { from: "sea", to: "anxiety", strength: 0.5 },
  { from: "ship", to: "adventure", strength: 0.9 },
  { from: "ship", to: "freedom", strength: 0.8 },
  { from: "ship", to: "peace", strength: 0.5 },
  { from: "car", to: "anxiety", strength: 0.7 },
  { from: "car", to: "freedom", strength: 0.6 },
  { from: "car", to: "confusion", strength: 0.5 },
  { from: "train", to: "adventure", strength: 0.7 },
  { from: "train", to: "anxiety", strength: 0.6 },
  { from: "train", to: "confusion", strength: 0.5 },
  { from: "airplane", to: "freedom", strength: 0.8 },
  { from: "airplane", to: "fear", strength: 0.7 },
  { from: "airplane", to: "adventure", strength: 0.6 },
  { from: "bicycle", to: "joy", strength: 0.7 },
  { from: "bicycle", to: "freedom", strength: 0.6 },
  { from: "bicycle", to: "peace", strength: 0.5 },
];

const STARS = Array.from({ length: 40 }, (_, i) => ({
  x: Math.random() * 100,
  y: Math.random() * 100,
  r: Math.random() * 1.2 + 0.3,
  opacity: Math.random() * 0.5 + 0.2,
  delay: Math.random() * 4,
}));

function getNodePos(node, width, height, padding) {
  return {
    cx: padding + (node.x / 100) * (width - padding * 2),
    cy: padding + (node.y / 100) * (height - padding * 2),
  };
}

export default function DreamMap() {
  const { t } = useTranslation();
  const [activeNode, setActiveNode] = useState(null);
  const [dimensions] = useState({ width: 800, height: 500 });
  const padding = 60;

  const allNodes = useMemo(() => [
    ...THEMES.map(n => ({ ...n, type: "theme" })),
    ...EMOTIONS.map(n => ({ ...n, type: "emotion" })),
  ], []);

  const nodeMap = useMemo(() => {
    const map = {};
    allNodes.forEach(n => { map[n.id] = n; });
    return map;
  }, [allNodes]);

  const activeConnections = useMemo(() => {
    if (!activeNode) return new Set();
    const set = new Set();
    CONNECTIONS.forEach(c => {
      if (c.from === activeNode || c.to === activeNode) {
        set.add(c.from);
        set.add(c.to);
      }
    });
    return set;
  }, [activeNode]);

  const handleNodeEnter = useCallback((id) => setActiveNode(id), []);
  const handleNodeLeave = useCallback(() => setActiveNode(null), []);

  return (
    <div className="relative w-full" data-testid="dream-map">
      <svg
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        className="w-full h-auto"
        style={{ maxHeight: "500px" }}
      >
        <defs>
          <radialGradient id="bgGrad" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="#1a1040" />
            <stop offset="100%" stopColor="#0a0820" />
          </radialGradient>
          <radialGradient id="themeGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#a78bfa" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="emotionGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#6ee7b7" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#6ee7b7" stopOpacity="0" />
          </radialGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="softGlow">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background */}
        <rect width={dimensions.width} height={dimensions.height} rx="20" fill="url(#bgGrad)" />

        {/* Stars */}
        {STARS.map((star, i) => (
          <circle
            key={`star-${i}`}
            cx={(star.x / 100) * dimensions.width}
            cy={(star.y / 100) * dimensions.height}
            r={star.r}
            fill="white"
            opacity={star.opacity}
          >
            <animate
              attributeName="opacity"
              values={`${star.opacity};${star.opacity * 0.3};${star.opacity}`}
              dur={`${3 + star.delay}s`}
              repeatCount="indefinite"
            />
          </circle>
        ))}

        {/* Connections */}
        {CONNECTIONS.map((conn, i) => {
          const fromNode = nodeMap[conn.from];
          const toNode = nodeMap[conn.to];
          if (!fromNode || !toNode) return null;
          const from = getNodePos(fromNode, dimensions.width, dimensions.height, padding);
          const to = getNodePos(toNode, dimensions.width, dimensions.height, padding);
          const isHighlighted = activeNode && (conn.from === activeNode || conn.to === activeNode);
          const isDimmed = activeNode && !isHighlighted;

          return (
            <line
              key={`conn-${i}`}
              x1={from.cx} y1={from.cy}
              x2={to.cx} y2={to.cy}
              stroke={isHighlighted ? "#c4b5fd" : "#4c3a8a"}
              strokeWidth={isHighlighted ? 2 : conn.strength * 1.2}
              strokeOpacity={isDimmed ? 0.08 : isHighlighted ? 0.8 : 0.2}
              strokeDasharray={isHighlighted ? "none" : "4 4"}
              style={{ transition: "all 0.4s ease" }}
            />
          );
        })}

        {/* Nodes */}
        {allNodes.map(node => {
          const pos = getNodePos(node, dimensions.width, dimensions.height, padding);
          const isTheme = node.type === "theme";
          const isActive = activeNode === node.id;
          const isConnected = activeConnections.has(node.id);
          const isDimmed = activeNode && !isConnected;
          const r = isTheme ? (isActive ? 18 : 14) : (isActive ? 14 : 10);
          const label = t(node.labelKey) !== node.labelKey ? t(node.labelKey) : node.defaultLabel;

          return (
            <g
              key={node.id}
              onMouseEnter={() => handleNodeEnter(node.id)}
              onMouseLeave={handleNodeLeave}
              onClick={() => setActiveNode(prev => prev === node.id ? null : node.id)}
              style={{ cursor: "pointer", transition: "opacity 0.4s ease" }}
              opacity={isDimmed ? 0.2 : 1}
            >
              {/* Outer glow on hover */}
              {(isActive || isConnected) && (
                <circle
                  cx={pos.cx} cy={pos.cy}
                  r={r + 12}
                  fill={isTheme ? "url(#themeGlow)" : "url(#emotionGlow)"}
                  style={{ transition: "all 0.3s ease" }}
                />
              )}
              {/* Node circle */}
              <circle
                cx={pos.cx} cy={pos.cy} r={r}
                fill={isTheme ? (isActive ? "#a78bfa" : "#7c3aed") : (isActive ? "#6ee7b7" : "#10b981")}
                stroke={isActive ? "white" : (isTheme ? "#c4b5fd" : "#a7f3d0")}
                strokeWidth={isActive ? 2 : 1}
                filter={isActive ? "url(#glow)" : "none"}
                style={{ transition: "all 0.3s ease" }}
              />
              {/* Inner dot */}
              <circle
                cx={pos.cx} cy={pos.cy}
                r={isTheme ? 4 : 3}
                fill="white"
                opacity={0.6}
              />
              {/* Label */}
              <text
                x={pos.cx}
                y={pos.cy + r + 14}
                textAnchor="middle"
                fill={isActive || isConnected ? "white" : "#a5b4c8"}
                fontSize={isTheme ? "11" : "10"}
                fontFamily="system-ui, sans-serif"
                fontWeight={isActive ? "600" : "400"}
                style={{ transition: "fill 0.3s ease", userSelect: "none" }}
              >
                {label}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="flex justify-center gap-6 mt-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-purple-500" />
          <span className="text-xs text-void/60 font-body">
            {t('dreamMap.themes') || 'Teemat'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-500" />
          <span className="text-xs text-void/60 font-body">
            {t('dreamMap.emotions') || 'Tunteet'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 border-t border-dashed border-void/30" />
          <span className="text-xs text-void/60 font-body">
            {t('dreamMap.connections') || 'Yhteydet'}
          </span>
        </div>
      </div>
    </div>
  );
}
